// The Evenfold — engine sandbox entry point. Deterministic simulation + thin Pixi
// renderer + white-box probe. Supports multiple rooms with fade and edge-pan exits.
import type { Container } from "pixi.js";
import { FixedClock } from "./core/clock";
import { RNG } from "./core/rng";
import { dist, pointInPolygon, type Polygon, type Vec2 } from "./core/geometry";
import * as sandboxWorld from "./rooms/testRoom";
import * as evenfoldWorld from "./rooms/evenfold";
import { analyzeQuest } from "./engine/quest";

// World selection: default = the synthetic sandbox (what every oracle test boots);
// `?world=evenfold` = the real game. Each world exports rooms/startRoomId/quest/view/scale.
// World selection: ?world=… query param wins; else the build-time default (VITE_WORLD, set by the
// single-file playtest build to "evenfold"); else the oracle sandbox (dev default + tests).
const worldParam = typeof location !== "undefined" ? new URLSearchParams(location.search).get("world") : null;
const worldName = worldParam ?? ((import.meta as { env?: Record<string, string> }).env?.VITE_WORLD) ?? "sandbox";
const WORLD = worldName === "evenfold" ? evenfoldWorld : sandboxWorld;
const ROOMS = WORLD.rooms;
const startRoomId = WORLD.startRoomId;
const QUEST = WORLD.quest;
import type { DialogLine, Exit, Hotspot, Npc, Room } from "./core/schema";
import { Nav } from "./engine/nav";
import { Actor } from "./engine/actor";
import { applyDepth, type Depthful } from "./engine/depth";
import { Renderer, type Dialog, type Prompt, type RoomHandle } from "./render/renderer";
import { installProbe, type TestState } from "./engine/testProbe";
import { validateWorld } from "./engine/validate";
import { holds, applyEffect, flameOf, type RoomState } from "./engine/roomState";

const centroid = (poly: Vec2[]): Vec2 => {
  const s = poly.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
  return { x: s.x / poly.length, y: s.y / poly.length };
};
const topCenter = (poly: Vec2[]): Vec2 => ({ x: centroid(poly).x, y: Math.min(...poly.map((p) => p.y)) });

// ---- Interactables: the room's hotspots + npcs, unified for proximity arming ----
// Hotspot anchors are static; npc anchors track the live actor (patrols move).
type Interactable =
  | { kind: "hotspot"; id: string; verb: string; anchor: Vec2; top: Vec2; poly: Vec2[] }
  | { kind: "npc"; id: string; verb: "talk"; npcIndex: number };

// ---- transition controller: fade (swap at black) or edge-pan (camera slide) ----
class Transition {
  kind: "idle" | "fade" | "pan" = "idle";
  alpha = 0;
  camX = 0;
  camY = 0;
  heroX = 0;
  heroY = 0;
  private t = 0;
  private dur = 0;
  private cf: Vec2 = { x: 0, y: 0 };
  private ct: Vec2 = { x: 0, y: 0 };
  private hf: Vec2 = { x: 0, y: 0 };
  private ht: Vec2 = { x: 0, y: 0 };
  private fadeMid: (() => void) | null = null;
  private fadeMidDone = false;
  private onEnd: (() => void) | null = null;

  get active(): boolean {
    return this.kind !== "idle";
  }
  startFade(dur: number, onMid: () => void): void {
    if (this.active) return;
    this.kind = "fade";
    this.dur = dur;
    this.t = 0;
    this.alpha = 0;
    this.fadeMid = onMid;
    this.fadeMidDone = false;
  }
  startPan(dur: number, cf: Vec2, ct: Vec2, hf: Vec2, ht: Vec2, onEnd: () => void): void {
    if (this.active) return;
    this.kind = "pan";
    this.dur = dur;
    this.t = 0;
    this.cf = cf;
    this.ct = ct;
    this.hf = hf;
    this.ht = ht;
    this.camX = cf.x;
    this.camY = cf.y;
    this.heroX = hf.x;
    this.heroY = hf.y;
    this.onEnd = onEnd;
  }
  reset(): void {
    this.kind = "idle";
    this.t = 0;
    this.alpha = 0;
    this.camX = 0;
    this.camY = 0;
    this.fadeMid = null;
    this.onEnd = null;
  }
  private ease(k: number): number {
    return k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
  }
  update(dt: number): void {
    if (this.kind === "idle") return;
    this.t += dt;
    const raw = Math.min(1, this.t / this.dur);
    if (this.kind === "fade") {
      if (raw < 0.5) {
        this.alpha = raw / 0.5;
      } else {
        if (!this.fadeMidDone) {
          this.fadeMid?.();
          this.fadeMidDone = true;
        }
        this.alpha = 1 - (raw - 0.5) / 0.5;
      }
      if (raw >= 1) this.reset();
    } else {
      const k = this.ease(raw);
      this.camX = this.cf.x + (this.ct.x - this.cf.x) * k;
      this.camY = this.cf.y + (this.ct.y - this.cf.y) * k;
      this.heroX = this.hf.x + (this.ht.x - this.hf.x) * k;
      this.heroY = this.hf.y + (this.ht.y - this.hf.y) * k;
      if (raw >= 1) {
        const end = this.onEnd;
        this.reset();
        end?.();
      }
    }
  }
}

interface Patrol {
  i: number; // index into npcActors
  pts: Vec2[];
  idx: number;
}
interface Runtime {
  room: Room;
  state: RoomState;
  nav: Nav;
  npcActors: Actor[];
  patrols: Patrol[];
  interactables: Interactable[];
}

// gated areas whose condition currently holds -> their polygons (folded into the navmesh)
const activeAreaPolys = (room: Room, state: RoomState, global: RoomState): Polygon[] =>
  room.gatedAreas.filter((a) => holds(a.gatedBy, state, global)).map((a) => a.polygon as Polygon);

async function boot(): Promise<void> {
  // GATE: no room loads unless the world validates (geometry + cross-room wiring).
  const audit = validateWorld(ROOMS);
  audit.warnings.forEach((w) => console.warn("[validate]", w));
  if (!audit.ok) throw new Error("Room validation failed:\n - " + audit.errors.join("\n - "));

  // ADVISORY: logical-completability analysis (win reachable? soft-locks? orphans?). Does
  // not block boot — it's a design report; per-room mechanics are gated by the oracles.
  const questReport = analyzeQuest(ROOMS, startRoomId, QUEST);
  console.log(`[quest] win reachable: ${questReport.winReachable}; reachable rooms: ${questReport.reachableRooms.length}/${Object.keys(ROOMS).length}`);
  questReport.warnings.forEach((w) => console.warn("[quest]", w));
  questReport.errors.forEach((e) => console.error("[quest]", e));

  const seed = 0x1234;
  const rng = new RNG(seed);
  const clock = new FixedClock(60);
  const transition = new Transition();

  const startRoom = ROOMS[startRoomId]!;
  const renderer = new Renderer();
  await renderer.init(WORLD.view, WORLD.scale, startRoom.background.color);
  // load Brand's directional pixel-art sprite + walk cycle (swaps in over the greybox figure)
  const HERO_DIRS = ["south", "north", "east", "west", "south-east", "north-east", "north-west", "south-west"];
  const BRAND_WALK: Record<string, string[]> = Object.fromEntries(
    HERO_DIRS.map((d) => [d, [0, 1, 2, 3, 4, 5].map((n) => `sprites/brand/walk/${d}/${n}.png`)]),
  );
  void renderer.loadHeroSprites("sprites/brand", BRAND_WALK);

  // GLOBAL state persists across room transitions (saga/quest flags). ROOM state ALSO
  // persists per session (a lever stays pulled, a hearth stays lit when you leave and
  // return) — kept in roomStates keyed by room id. Both cleared only by a full reset.
  let globalState: RoomState = { ...(QUEST.initialGlobal ?? {}) };
  let roomStates: Record<string, RoomState> = {};

  const makeRuntime = (room: Room): Runtime => {
    const npcActors = room.npcs.map(
      (n) => new Actor({ x: n.x, y: n.y }, { radius: n.radius, movable: !!n.patrol, speed: n.patrol ? n.speed : 0 }),
    );
    const patrols: Patrol[] = [];
    room.npcs.forEach((n, i) => {
      if (n.patrol && n.patrol.length) patrols.push({ i, pts: n.patrol, idx: 0 });
    });
    // reuse this room's persisted state if we've been here before this session; else seed
    // from the authored initial state. Mutations write through to roomStates, so progress
    // (levers pulled, hearths lit) survives leaving and returning.
    const state: RoomState = roomStates[room.id] ?? (roomStates[room.id] = { ...room.state });
    const nav = new Nav(room, activeAreaPolys(room, state, globalState));
    return { room, state, nav, npcActors, patrols, interactables: buildInteractables(room, state, nav) };
  };

  // Interactables = active (ungated or condition-holding) hotspots + npcs. Rebuilt when
  // state changes, since gating can switch hotspots on/off.
  function buildInteractables(room: Room, state: RoomState, nav: Nav): Interactable[] {
    return [
      ...room.hotspots
        .filter((h) => holds(h.gatedBy, state, globalState))
        .map((h): Interactable => ({
          kind: "hotspot",
          id: h.id,
          verb: h.verb,
          anchor: h.interactAt ? { x: h.interactAt.x, y: h.interactAt.y } : nav.clamp(centroid(h.polygon)),
          top: topCenter(h.polygon),
          poly: h.polygon,
        })),
      ...room.npcs.map((n, i): Interactable => ({ kind: "npc", id: n.id, verb: "talk", npcIndex: i })),
    ];
  }

  const actor = new Actor(startRoom.spawn, { radius: startRoom.actorRadius });
  let current = makeRuntime(startRoom);
  let currentHandle = renderer.buildRoom(startRoom, { x: 0, y: 0 });
  renderer.setRoomSize(startRoom.size.w, startRoom.size.h); // letterbox centring for the start room
  let nextHandle: RoomHandle | null = null;

  // ---- depth registration for the current room (offset 0) ----
  const depthItems: Depthful[] = [];
  const nodeLabel = new Map<Container, string>();
  function buildDepth(): void {
    depthItems.length = 0;
    nodeLabel.clear();
    let bias = 0;
    for (const occ of current.room.occluders) {
      const node = currentHandle.occluderNodes.get(occ.id)!;
      depthItems.push({ node, baseline: () => occ.baselineY, bias: bias++ });
      nodeLabel.set(node, occ.id);
    }
    current.room.npcs.forEach((n, i) => {
      const node = currentHandle.npcNodes.get(n.id)!;
      const a = current.npcActors[i]!;
      depthItems.push({ node, baseline: () => a.feetY, bias: bias++ });
      nodeLabel.set(node, n.id);
    });
    depthItems.push({ node: renderer.hero, baseline: () => actor.feetY, bias: bias++ });
    nodeLabel.set(renderer.hero, "hero");
  }
  buildDepth();

  // ---- mutable view/probe state ----
  const ev: { step: number; x: number; y: number; kind: string }[] = [];
  let target: Vec2 | null = null;
  let targetKind: TestState["targetKind"] = null;
  let clamped = false;
  let lastClick: Vec2 | null = null;
  let lastInteraction: TestState["lastInteraction"] = null;
  let pendingExit: Exit | null = null;
  let pendingInteract: Interactable | null = null; // clicked an interactable -> fire on arrival
  let armed: Interactable | null = null; // nearest in-range, in-facing interactable
  // an open wordless speech bubble (talking to an npc); advanced by interact/click
  let activeDialog: { npcIndex: number; npcId: string; lines: DialogLine[]; index: number } | null = null;
  let clickCount = 0;
  let stuck = 0;
  let prevHero: Vec2 = { x: actor.pos.x, y: actor.pos.y };
  let hover: { kind: string; id: string | null; poly: Vec2[] | null } = { kind: "none", id: null, poly: null };
  const ripples: { x: number; y: number; age: number }[] = [];

  // text-first caption: prose NPC dialogue is driven by `activeDialog`; this holds the transient
  // examine line / room-entry card (persists until the next click or interaction).
  let caption: { text: string; color: number } | null = null;
  const MOOD_COLOR: Record<string, number> = {
    neutral: 0xf0e6d0, warm: 0xffcf9e, wary: 0xc9b3e0, sad: 0x9fb6d6, glad: 0xb6d6a0,
  };
  const showLine = (text: string, color = 0xf0e6d0): void => { caption = { text, color }; };
  const showRoomCard = (): void => { if (current.room.name) caption = { text: current.room.name, color: 0xe8dcc0 }; };
  showRoomCard(); // the start room's title card

  const CURSOR: Record<string, string> = {
    hotspot: "pointer",
    exit: "pointer",
    npc: "pointer",
    walk: "default",
    blocked: "not-allowed",
    none: "default",
  };

  const chars = (): Actor[] => [actor, ...current.npcActors];

  // gating: a hotspot/exit is only live when its condition holds (room or global state).
  function hotspotAt(p: Vec2): Hotspot | null {
    const hits = current.room.hotspots.filter(
      (h) => holds(h.gatedBy, current.state, globalState) && pointInPolygon(p, h.polygon),
    );
    hits.sort((a, b) => b.priority - a.priority);
    return hits[0] ?? null;
  }
  function exitAt(p: Vec2): Exit | null {
    return (
      current.room.exits.find((e) => holds(e.gatedBy, current.state, globalState) && pointInPolygon(p, e.polygon)) ??
      null
    );
  }

  /** Re-derive everything that depends on state (after an interaction effect). */
  function onStateChanged(): void {
    current.nav = new Nav(current.room, activeAreaPolys(current.room, current.state, globalState));
    current.interactables = buildInteractables(current.room, current.state, current.nav);
    armed = computeArmed();
  }

  // ---- interactables: live position helpers (npc anchors track the moving actor) ----
  const npcPos = (i: number): Vec2 => current.npcActors[i]!.pos;
  function anchorOf(it: Interactable): Vec2 {
    return it.kind === "hotspot" ? it.anchor : npcPos(it.npcIndex);
  }
  function glyphOf(it: Interactable): Vec2 {
    if (it.kind === "hotspot") return it.top;
    const p = npcPos(it.npcIndex);
    return { x: p.x, y: p.y - 30 }; // above the npc's head
  }
  function promptOf(it: Interactable): Prompt {
    const g = glyphOf(it);
    if (it.kind === "hotspot") return { x: g.x, y: g.y, verb: it.verb, poly: it.poly, feet: null };
    const f = npcPos(it.npcIndex);
    return { x: g.x, y: g.y, verb: "talk", poly: null, feet: { x: f.x, y: f.y } };
  }
  function interactableById(kind: "hotspot" | "npc", id: string): Interactable | null {
    return current.interactables.find((it) => it.kind === kind && it.id === id) ?? null;
  }
  /** Generous pick of an npc near a click (reward intent; no pixel hunting). */
  function npcAt(p: Vec2): Interactable | null {
    let best: Interactable | null = null;
    let bestD = Infinity;
    current.room.npcs.forEach((n, i) => {
      const d = dist(p, npcPos(i));
      const pick = n.radius + current.room.actorRadius + 6;
      if (d <= pick && d < bestD) {
        bestD = d;
        best = interactableById("npc", n.id);
      }
    });
    return best;
  }

  /** The armed target: nearest in-range interactable, biased toward the hero's facing.
   * Pure function of (actor pos, facing, room) — deterministic. */
  function computeArmed(): Interactable | null {
    if (transition.active || activeDialog) return null; // mid-conversation: nothing arms
    const R = current.room.interactRadius;
    let best: Interactable | null = null;
    let bestScore = Infinity;
    for (const it of current.interactables) {
      const a = anchorOf(it);
      const d = dist(actor.pos, a);
      if (d > R) continue;
      const dx = a.x - actor.pos.x;
      const inFacing = Math.abs(dx) < 4 || Math.sign(dx) === actor.facing;
      const score = d * (inFacing ? 1 : 1.6);
      if (score < bestScore - 1e-9 || (Math.abs(score - bestScore) <= 1e-9 && (!best || it.id < best.id))) {
        bestScore = score;
        best = it;
      }
    }
    return best;
  }

  /** Fire the context-sensitive interaction on a specific interactable. Applies the
   * hotspot's declarative effect to room state, then re-derives state-dependent systems. */
  function fireInteract(it: Interactable): void {
    const g = glyphOf(it);
    if (Math.abs(g.x - actor.pos.x) > 0.5) actor.facing = g.x < actor.pos.x ? -1 : 1;
    lastInteraction = { kind: it.kind, id: it.id, verb: it.verb, step: clock.step };
    ev.push({ step: clock.step, x: anchorOf(it).x, y: anchorOf(it).y, kind: `interact:${it.verb}:${it.id}` });
    if (it.kind === "hotspot") {
      const hs = current.room.hotspots.find((h) => h.id === it.id);
      const changed = applyEffect(hs?.effect, current.state, globalState);
      if (changed.length) onStateChanged();
      if (hs?.line) showLine(hs.line); // the prose description (text-first)
    } else if (it.kind === "npc") {
      openDialog(it.npcIndex); // prose dialogue, if this npc has any lines
    }
  }

  // ---- wordless dialogue ----
  function openDialog(npcIndex: number): void {
    const npc = current.room.npcs[npcIndex];
    if (!npc?.dialog || !npc.dialog.length) return; // no lines -> nothing opens
    activeDialog = { npcIndex, npcId: npc.id, lines: npc.dialog, index: 0 };
  }
  function advanceDialog(): void {
    if (!activeDialog) return;
    activeDialog.index++;
    if (activeDialog.index >= activeDialog.lines.length) activeDialog = null; // past the last line -> close
  }

  /** The interact button: advance an open bubble, else fire the armed interactable. */
  function doInteract(): void {
    if (activeDialog) {
      advanceDialog();
      return;
    }
    const it = computeArmed();
    if (!it) return;
    pendingInteract = null;
    fireInteract(it);
  }
  function setHover(p: Vec2): void {
    if (transition.active) {
      hover = { kind: "none", id: null, poly: null };
      return;
    }
    // precedence matches resolveClick: hotspot > exit > npc > walk/blocked
    const hs = hotspotAt(p);
    if (hs) hover = { kind: "hotspot", id: hs.id, poly: hs.polygon };
    else {
      const ex = exitAt(p);
      if (ex) hover = { kind: "exit", id: ex.id, poly: ex.polygon };
      else {
        const npc = npcAt(p);
        if (npc) hover = { kind: "npc", id: npc.id, poly: null };
        else if (current.nav.isWalkable(p)) hover = { kind: "walk", id: null, poly: null };
        else hover = { kind: "blocked", id: null, poly: null };
      }
    }
    renderer.setCursor(CURSOR[hover.kind] ?? "default");
  }

  function resolveClick(p: Vec2): void {
    if (transition.active) return;
    if (activeDialog) {
      advanceDialog(); // a bubble is open — a click turns the page, it doesn't move the hero
      return;
    }
    caption = null; // a fresh click dismisses any examine line / room card
    lastClick = { x: p.x, y: p.y };
    clickCount++;
    ripples.push({ x: p.x, y: p.y, age: 0 }); // immediate click acknowledgement
    const hs = hotspotAt(p);
    const ex = exitAt(p);
    // hotspot > exit > npc > bare walk (matches hover precedence; npc is a generous pick)
    if (hs) {
      const it = interactableById("hotspot", hs.id)!;
      const r = current.nav.findPath(actor.pos, anchorOf(it));
      actor.setPath(r.path);
      target = r.goal;
      clamped = r.clamped;
      targetKind = "hotspot";
      pendingInteract = it; // fire the interaction when the hero arrives
      pendingExit = null;
      ev.push({ step: clock.step, x: p.x, y: p.y, kind: "hotspot:" + hs.id });
      return;
    }
    if (ex) {
      const r = current.nav.findPath(actor.pos, centroid(ex.polygon));
      actor.setPath(r.path);
      target = r.goal;
      clamped = r.clamped;
      targetKind = "exit";
      pendingExit = ex;
      pendingInteract = null;
      ev.push({ step: clock.step, x: p.x, y: p.y, kind: "exit:" + ex.id });
      return;
    }
    const npc = npcAt(p);
    if (npc) {
      const r = current.nav.findPath(actor.pos, anchorOf(npc));
      actor.setPath(r.path);
      target = r.goal;
      clamped = r.clamped;
      targetKind = "npc";
      pendingInteract = npc; // walk up to the npc, then talk
      pendingExit = null;
      ev.push({ step: clock.step, x: p.x, y: p.y, kind: "npc:" + npc.id });
      return;
    }
    const r = current.nav.findPath(actor.pos, p);
    actor.setPath(r.path);
    target = r.goal;
    clamped = r.clamped;
    targetKind = "walk";
    pendingExit = null;
    pendingInteract = null;
    ev.push({ step: clock.step, x: p.x, y: p.y, kind: "walk" });
  }

  function switchRoom(targetId: string, spawn: Vec2): void {
    const next = ROOMS[targetId];
    if (!next) return;
    renderer.destroyRoom(currentHandle);
    currentHandle = renderer.buildRoom(next, { x: 0, y: 0 });
    current = makeRuntime(next);
    renderer.setRoomSize(next.size.w, next.size.h); // letterbox centring for this room
    actor.pos = { x: spawn.x, y: spawn.y };
    actor.stop();
    target = null;
    targetKind = null;
    pendingInteract = null;
    armed = null;
    activeDialog = null;
    buildDepth();
    caption = null;
    showRoomCard(); // the new room's title card
    renderer.setCamera(0, 0);
    renderer.setHeroZ(0); // applyDepth restores the real value next frame
  }

  /** Hard-jump to a room at its spawn (no transition) — used by the probe `goto` and the
   * dev number-key room switcher. Clears all pending/transient state cleanly. */
  function jumpToRoom(roomId: string): void {
    const r = ROOMS[roomId];
    if (!r) return;
    transition.reset();
    if (nextHandle) {
      renderer.destroyRoom(nextHandle);
      nextHandle = null;
    }
    switchRoom(roomId, r.spawn);
    pendingExit = null;
    target = null;
    targetKind = null;
    lastClick = null;
    hover = { kind: "none", id: null, poly: null };
    ripples.length = 0;
    stuck = 0;
    prevHero = { x: actor.pos.x, y: actor.pos.y };
    syncView();
  }

  function startTransition(ex: Exit): void {
    if (ex.transition === "edge-pan" && ROOMS[ex.target]) {
      const pan = ex.pan ?? { x: current.room.size.w, y: 0 };
      nextHandle = renderer.buildRoom(ROOMS[ex.target]!, pan);
      renderer.setHeroZ(900_000); // hero rides on top across the seam
      transition.startPan(
        0.85,
        { x: 0, y: 0 },
        pan,
        { x: actor.pos.x, y: actor.pos.y },
        { x: ex.spawn.x + pan.x, y: ex.spawn.y + pan.y },
        () => {
          if (nextHandle) {
            renderer.destroyRoom(nextHandle);
            nextHandle = null;
          }
          switchRoom(ex.target, ex.spawn);
        },
      );
    } else {
      transition.startFade(0.7, () => switchRoom(ex.target, ex.spawn));
    }
  }

  // ---- character separation (deterministic) ----
  function resolvePair(a: Actor, b: Actor): void {
    let dx = b.pos.x - a.pos.x;
    let dy = b.pos.y - a.pos.y;
    let d = Math.hypot(dx, dy);
    const minD = a.radius + b.radius;
    if (d >= minD) return;
    if (d < 1e-4) {
      dx = minD;
      dy = 0;
      d = minD;
    }
    const nx = dx / d;
    const ny = dy / d;
    const pen = minD - d;
    const wa = a.movable ? 1 : 0;
    const wb = b.movable ? 1 : 0;
    const tot = wa + wb;
    if (tot === 0) return;
    a.pos = { x: a.pos.x - nx * pen * (wa / tot), y: a.pos.y - ny * pen * (wa / tot) };
    b.pos = { x: b.pos.x + nx * pen * (wb / tot), y: b.pos.y + ny * pen * (wb / tot) };
  }
  function separate(cs: Actor[]): void {
    for (let iter = 0; iter < 2; iter++) {
      for (let i = 0; i < cs.length; i++) {
        for (let j = i + 1; j < cs.length; j++) resolvePair(cs[i]!, cs[j]!);
      }
    }
    for (const c of cs) if (c.movable) c.pos = current.nav.ground(c.pos);
  }

  // simulation step (fixed dt)
  function step(dt: number): void {
    if (transition.active) {
      transition.update(dt);
      return;
    }
    // advance patrols: idle wanderers head to their next looped waypoint
    for (const p of current.patrols) {
      const a = current.npcActors[p.i]!;
      if (!a.moving) {
        p.idx = (p.idx + 1) % p.pts.length;
        a.setPath([{ x: p.pts[p.idx]!.x, y: p.pts[p.idx]!.y }]);
      }
    }
    const cs = chars();
    for (const c of cs) c.update(dt);
    separate(cs);
    if (actor.moving && dist(actor.pos, prevHero) < 0.15) {
      if (++stuck > 20) {
        actor.stop();
        stuck = 0;
      }
    } else {
      stuck = 0;
    }
    prevHero = { x: actor.pos.x, y: actor.pos.y };
    if (pendingExit && !actor.moving) {
      const ex = pendingExit;
      pendingExit = null;
      startTransition(ex);
    }
    // arrived at (or stuck beside) a clicked interactable -> fire it if in range
    if (pendingInteract && !actor.moving) {
      const it = pendingInteract;
      pendingInteract = null;
      if (dist(actor.pos, anchorOf(it)) <= current.room.interactRadius) fireInteract(it);
    }
  }

  // input
  renderer.app.stage.eventMode = "static";
  renderer.app.stage.hitArea = renderer.app.screen;
  renderer.app.stage.on("pointerdown", (e) => {
    if (transition.kind === "pan") return; // world is mid-slide; ignore
    const local = renderer.world.toLocal(e.global);
    resolveClick({ x: local.x, y: local.y });
  });
  renderer.app.stage.on("pointermove", (e) => {
    const local = renderer.world.toLocal(e.global);
    setHover({ x: local.x, y: local.y });
  });
  const roomIds = Object.keys(ROOMS); // stable insertion order -> number keys 1..N
  window.addEventListener("keydown", (e) => {
    if (e.key.toLowerCase() === "d") renderer.toggleDebug();
    if (e.key === " " || e.key.toLowerCase() === "e") {
      e.preventDefault();
      doInteract();
    }
    // dev room switcher: keys 1..9 jump straight to a sandbox room
    if (e.key >= "1" && e.key <= "9") {
      const idx = Number(e.key) - 1;
      if (idx < roomIds.length) jumpToRoom(roomIds[idx]!);
    }
  });

  const hud = document.getElementById("hud")!;
  function depthOrder(): string[] {
    return [...depthItems].sort((a, b) => a.node.zIndex - b.node.zIndex).map((it) => nodeLabel.get(it.node) ?? "?");
  }

  // Tracking camera: center the hero, clamped to the room so we never show beyond it.
  // For viewport-sized rooms this is always (0,0) — backward compatible.
  let camX = 0;
  let camY = 0;
  function cameraFollow(): void {
    const vw = renderer.viewW / renderer.scale;
    const vh = renderer.viewH / renderer.scale;
    const rw = current.room.size.w;
    const rh = current.room.size.h;
    camX = Math.max(0, Math.min(actor.pos.x - vw / 2, Math.max(0, rw - vw)));
    camY = Math.max(0, Math.min(actor.pos.y - vh / 2, Math.max(0, rh - vh)));
  }

  function syncView(): void {
    if (transition.kind === "pan") {
      camX = transition.camX;
      camY = transition.camY;
      renderer.setCamera(camX, camY);
      renderer.setHeroWorld(transition.heroX, transition.heroY);
      renderer.setHeroPose(1, 0, true, performance.now()); // riding east across the seam
    } else {
      cameraFollow();
      renderer.setCamera(camX, camY);
      renderer.setHeroWorld(actor.pos.x, actor.pos.y);
      // direction from the heading toward the next path waypoint (prevHero is updated per-step, so a
      // pos-delta here is always ~0 -> the hero would never turn from south)
      const next = actor.remaining[0];
      renderer.setHeroPose(next ? next.x - actor.pos.x : 0, next ? next.y - actor.pos.y : 0, actor.moving, performance.now());
      applyDepth(depthItems);
    }
    current.room.npcs.forEach((n, i) => {
      const node = currentHandle.npcNodes.get(n.id)!;
      const a = current.npcActors[i]!;
      node.x = a.pos.x;
      node.y = a.pos.y;
    });
    renderer.setFade(transition.alpha);
    renderer.setOpenAreas(
      currentHandle,
      new Set(current.room.gatedAreas.filter((a) => holds(a.gatedBy, current.state, globalState)).map((a) => a.id)),
    );
    const handles = nextHandle ? [currentHandle, nextHandle] : [currentHandle];
    renderer.drawDebug(handles, { path: actor.remaining, target, clamped, lastClick });
    renderer.drawFx({ kind: hover.kind, poly: hover.poly }, ripples, performance.now());
    // the carried flame's glow follows the hero (rides on top during an edge-pan)
    const flame = flameOf(globalState);
    const lightR = flame > 0 ? 24 + flame * 14 : 0;
    const lx = transition.kind === "pan" ? transition.heroX : actor.pos.x;
    const ly = transition.kind === "pan" ? transition.heroY : actor.pos.y;
    renderer.drawLight(lx, ly, lightR, performance.now());
    // living-scene ambient effects for the current room (cosmetic; real-time, not sim-locked)
    renderer.drawAmbient(current.room.fx, performance.now());
    renderer.updateFire(currentHandle, performance.now());
    if (nextHandle) renderer.updateFire(nextHandle, performance.now());
    armed = computeArmed();
    // while a bubble is open, suppress the armed prompt and show the speech bubble instead
    renderer.drawPrompt(activeDialog || !armed ? null : promptOf(armed), performance.now());
    // text-first: a prose dialogue line drives the caption (above any transient examine/room card);
    // the legacy glyph bubble is drawn only when a line carries glyphs (decorative / sandbox).
    const dline = activeDialog ? activeDialog.lines[activeDialog.index]! : null;
    const more = activeDialog ? activeDialog.index < activeDialog.lines.length - 1 : false;
    if (dline?.text) renderer.setCaption(more ? dline.text + "  ▸" : dline.text, MOOD_COLOR[dline.mood] ?? MOOD_COLOR.neutral);
    else if (caption) renderer.setCaption(caption.text, caption.color);
    else renderer.setCaption(null);
    if (dline && dline.glyphs && dline.glyphs.length) {
      const a = current.npcActors[activeDialog!.npcIndex]!;
      renderer.drawDialog({ x: a.pos.x, y: a.pos.y - 28, glyphs: dline.glyphs, mood: dline.mood, more }, performance.now());
    } else {
      renderer.drawDialog(null, 0);
    }
    const order = depthOrder();
    hud.textContent =
      `room ${current.room.id}  step ${clock.step}  seed 0x${seed.toString(16)}\n` +
      `actor ${actor.pos.x.toFixed(1)},${actor.pos.y.toFixed(1)} ${actor.moving ? "→ moving" : "idle"}\n` +
      `target ${target ? `${target.x.toFixed(0)},${target.y.toFixed(0)} ${clamped ? "(clamped)" : ""}` : "-"} [${targetKind ?? "-"}]\n` +
      `transition ${transition.kind}  clicks ${clickCount}\n` +
      `flame ${flame}  armed ${armed ? `${armed.verb}:${armed.id}` : "-"}   [Space/E]=interact\n` +
      `depth(back→front) ${order.join(" < ")}\n` +
      `last ${lastInteraction ? `${lastInteraction.verb}:${lastInteraction.id}` : "-"}   [D]=debug  [1-${roomIds.length}]=rooms`;
  }

  function state(): TestState {
    return {
      roomId: current.room.id,
      step: clock.step,
      seed,
      actor: { x: actor.pos.x, y: actor.pos.y, moving: actor.moving, facing: actor.facing },
      target,
      targetKind,
      clamped,
      lastClick,
      lastInteraction,
      armed: armed ? { kind: armed.kind, id: armed.id, verb: armed.verb } : null,
      state: { ...current.state },
      globalState: { ...globalState },
      flame: flameOf(globalState),
      won: QUEST.win.every((c) => holds(c, current.state, globalState)),
      openAreas: current.room.gatedAreas.filter((a) => holds(a.gatedBy, current.state, globalState)).map((a) => a.id),
      // the prose currently on screen: an open dialogue line, else a transient examine/room card
      caption: activeDialog?.lines[activeDialog.index]?.text ?? caption?.text ?? null,
      roomName: current.room.name ?? null,
      dialog: activeDialog
        ? {
            npcId: activeDialog.npcId,
            index: activeDialog.index,
            total: activeDialog.lines.length,
            text: activeDialog.lines[activeDialog.index]!.text ?? null,
            glyphs: activeDialog.lines[activeDialog.index]!.glyphs ?? null,
            mood: activeDialog.lines[activeDialog.index]!.mood,
          }
        : null,
      fade: transition.kind,
      depthOrder: depthOrder(),
      npcs: current.room.npcs.map((n, i) => ({ id: n.id, x: current.npcActors[i]!.pos.x, y: current.npcActors[i]!.pos.y })),
      hover: { kind: hover.kind, id: hover.id },
      camera: { x: camX, y: camY },
      view: { w: renderer.viewW / renderer.scale, h: renderer.viewH / renderer.scale },
      room: { w: current.room.size.w, h: current.room.size.h },
      clickCount,
    };
  }

  let paused = false;
  function resetSim(): void {
    clock.reset();
    rng.restore(seed);
    globalState = { ...(QUEST.initialGlobal ?? {}) }; // new game — back to the starting saga flags
    roomStates = {}; // ...and per-room progress
    renderer.destroyRoom(currentHandle);
    if (nextHandle) {
      renderer.destroyRoom(nextHandle);
      nextHandle = null;
    }
    transition.reset();
    currentHandle = renderer.buildRoom(startRoom, { x: 0, y: 0 });
    current = makeRuntime(startRoom);
    actor.pos = { x: startRoom.spawn.x, y: startRoom.spawn.y };
    actor.stop();
    buildDepth();
    renderer.setCamera(0, 0);
    target = null;
    targetKind = null;
    clamped = false;
    lastClick = null;
    lastInteraction = null;
    pendingExit = null;
    pendingInteract = null;
    armed = null;
    activeDialog = null;
    clickCount = 0;
    ev.length = 0;
    ripples.length = 0;
    hover = { kind: "none", id: null, poly: null };
    stuck = 0;
    prevHero = { x: startRoom.spawn.x, y: startRoom.spawn.y };
    paused = true;
    syncView();
  }

  installProbe({
    version: 1,
    state,
    log: () => ev,
    click: (x, y) => resolveClick({ x, y }),
    hover: (x, y) => setHover({ x, y }),
    interact: () => doInteract(),
    goto: (roomId) => jumpToRoom(roomId),
    room: () => current.room,
    tick: (n) => {
      for (let i = 0; i < n; i++) clock.tick(step);
      syncView();
    },
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
    },
    reset: resetSim,
  });

  renderer.app.ticker.add(() => {
    const dtReal = renderer.app.ticker.deltaMS / 1000;
    if (!paused) clock.advance(dtReal, step);
    // age click ripples (purely cosmetic, real-time)
    for (const r of ripples) r.age += dtReal;
    for (let i = ripples.length - 1; i >= 0; i--) if (ripples[i]!.age >= 0.45) ripples.splice(i, 1);
    syncView();
  });

  void rng;
  syncView();
  console.log("[evenfold] sandbox booted:", current.room.id);
}

boot().catch((e) => {
  console.error(e);
  const hud = document.getElementById("hud");
  if (hud) hud.textContent = "BOOT ERROR: " + (e?.message ?? e);
});
